import { Auth0Provider } from "@auth0/auth0-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import Hotjar from "@hotjar/browser";
import { OktaAuth } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import * as Sentry from "@sentry/react";
import { msalConfig } from "authConfig";
import { EnvNames } from "components/Header/TopHeader/EnvironmentBadge/EnvironmentBadge";
import { oktaConfig } from "oktaConfig";
import ReactDOM from "react-dom";
import TagManager from "react-gtm-module";
import { getEnvVariable } from "utilities/env";
import { App } from "./App";
import * as serviceWorker from "./serviceWorker";

const domain = getEnvVariable("REACT_APP_AUTH0_DOMAIN");
const clientId = getEnvVariable("REACT_APP_AUTH0_CLIENT_ID");
const gtmId = getEnvVariable("REACT_APP_GTM_ID");
const environment = getEnvVariable("REACT_APP_SENTRY_ENVIRONMENT");
const sentryDSN = getEnvVariable("REACT_APP_SENTRY_DSN");
const hotjarSiteId = getEnvVariable("REACT_APP_HOTJAR_SITE_ID");

const MAClientId = getEnvVariable("REACT_APP_MA_CLIENT_ID");
const MADirectoryId = getEnvVariable("REACT_APP_MA_PROVIDER_APP_URL");

const OKTAClientId = getEnvVariable("REACT_APP_OKTA_CLIENT_ID");
const OKTADirectoryId = getEnvVariable("REACT_APP_OKTA_PROVIDER_APP_URL");

export type AvailableFeatureFlags = "UNPAID_INVOICE" | "LEADERBOARDS";

type CommaSeparatedFeatureFlags =
  | AvailableFeatureFlags
  | `${AvailableFeatureFlags},${string}`;

export interface RuntimeEnv {
  // API and Core Configuration
  REACT_APP_API_URL: string;
  REACT_APP_AUTOMATIC_LOGOUT: string;
  REACT_APP_COOKIE_EXPIRATION_HOURS: string;

  // Authentication Providers
  // Auth0
  REACT_APP_AUTH0_DOMAIN: string;
  REACT_APP_AUTH0_CLIENT_ID: string;
  // Microsoft Azure
  REACT_APP_MA_CLIENT_ID: string;
  REACT_APP_MA_PROVIDER_APP_URL: string;
  // OKTA
  REACT_APP_OKTA_CLIENT_ID: string;
  REACT_APP_OKTA_PROVIDER_APP_URL: string;

  // Analytics and Monitoring
  REACT_APP_GTM_ID: string;
  REACT_APP_HOTJAR_SITE_ID: string;

  // Feature Configuration
  REACT_APP_FEATURE_FLAGS: CommaSeparatedFeatureFlags;

  // Business Logic Limits
  REACT_APP_TIER_SET_ANNUAL_RECALCULATION_DATES_LIMIT: string;
  REACT_APP_CAMPAIGN_TRANSACTION_ITEMS_FILTERS_MAX: string;

  // Sentry Error Tracking
  REACT_APP_SENTRY_AUTH_TOKEN: string;
  REACT_APP_SENTRY_DSN: string;
  REACT_APP_SENTRY_ENVIRONMENT: `${EnvNames}`;
  REACT_APP_SENTRY_INSTANCE: string;
  REACT_APP_SENTRY_PROFILES_SAMPLE_RATE: string;
  REACT_APP_SENTRY_RELEASE: string;
  REACT_APP_SENTRY_TRACES_SAMPLE_RATE: string;

  // Documentation
  REACT_APP_GITBOOK_TOKEN: string;
  REACT_APP_GITBOOK_SPACE_ID: string;
}
if (hotjarSiteId) {
  const hotjarVersion = 6;

  Hotjar.init(Number(hotjarSiteId), hotjarVersion);
}

if (gtmId) {
  const tagManagerArgs = {
    gtmId,
    dataLayer: [],
  };

  TagManager.initialize(tagManagerArgs);
}

declare global {
  interface Window {
    runtime_env: RuntimeEnv;
  }
}
if (sentryDSN && (environment === "prod" || environment === "qa")) {
  Sentry.init({
    dsn: sentryDSN,
    environment: environment,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(environment === "prod"
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
              unmask: ["[data-sentry-unmask]"],
            }),
          ]
        : []),
    ],
    tracesSampleRate: 1.0,
    ignoreErrors: [
      "ResizeObserver loop completed with undelivered notifications.",
    ],
  });
}

if (domain && clientId) {
  ReactDOM.render(
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
      }}
    >
      <App />
    </Auth0Provider>,
    document.getElementById("root"),
  );
} else if (MAClientId && MADirectoryId) {
  const msalInstance = new PublicClientApplication(msalConfig);

  ReactDOM.render(
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>,
    document.getElementById("root"),
  );
} else if (OKTAClientId && OKTADirectoryId) {
  const oktaAuth = new OktaAuth(oktaConfig);

  ReactDOM.render(
    <Security oktaAuth={oktaAuth} restoreOriginalUri={() => {}}>
      <App />
    </Security>,
    document.getElementById("root"),
  );
} else {
  ReactDOM.render(<App />, document.getElementById("root"));
}

serviceWorker.unregister();
